import { IconButton } from '@feral/ui'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { createContext, useContext, useMemo, useSyncExternalStore } from 'react'
import {
  home,
  PIN_DEFAULT_SIDE,
  PIN_PATHS,
  type Pin,
  type PinPath,
  type PinSide,
  usePrefs,
} from '../lib/prefs.js'

/**
 * Pinning (bugs.md): at tablet and desktop widths a tab popup (Gate, Wall, Inbox) can dock to the
 * left or right edge and stay open while the map moves; on a wide screen two can, one on each side.
 * The docked copy renders outside the router outlet, so other popups still open over it.
 */
const WIDE = '(min-width: 600px)'
/** Room for a panel on each side and the map between (map.css keeps the same number). */
const WIDER = '(min-width: 1000px)'
const media = (q: string) => ({
  subscribe: (cb: () => void) => {
    const mq = window.matchMedia(q)
    mq.addEventListener('change', cb)
    return () => mq.removeEventListener('change', cb)
  },
  get: () => window.matchMedia(q).matches,
})
const wideMq = media(WIDE)
const widerMq = media(WIDER)
export function useWide() {
  return useSyncExternalStore(wideMq.subscribe, wideMq.get)
}

/** How many tabs can be docked at this width: none on a phone, one on a tablet, two on a wide screen. */
export function useRoom() {
  const w = useWide()
  const ww = useSyncExternalStore(widerMq.subscribe, widerMq.get)
  return !w ? 0 : ww ? 2 : 1
}

/** The pins showing now: none at phone width, one at tablet width, whatever the prefs say otherwise. */
export function usePinned(): Pin[] {
  const pinned = usePrefs((s) => s.pinned)
  const room = useRoom()
  return useMemo(() => pinned.slice(0, room), [pinned, room])
}

/** The tab docked in this panel, or null outside one, so its screen closes by unpinning and keeps its URL alone. */
export const PinnedContext = createContext<PinPath | null>(null)
export const useDockedPath = () => useContext(PinnedContext)
export const useInPinned = () => useContext(PinnedContext) !== null

/** `pinned` with this tab docked on `side`, whoever was there moving over (to the other side, or off). */
export function pinTo(pinned: Pin[], path: PinPath, side: PinSide, room: number): Pin[] {
  const rest = pinned.filter((p) => p.path !== path)
  const other: PinSide = side === 'left' ? 'right' : 'left'
  const there = rest.find((p) => p.side === side)
  if (!there) return room > 1 || rest.length === 0 ? [...rest, { path, side }] : [{ path, side }]
  // Taking the side of another pin: it goes across if there's room for two and it was alone, else off.
  const moved = room > 1 && rest.length === 1 ? [{ ...there, side: other }] : []
  return [...moved, { path, side }]
}

const pinIcon = (
  <svg
    viewBox="0 0 24 24"
    width={20}
    height={20}
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 4h6l-1 5 3 3v2H7v-2l3-3-1-5Z" />
    <path d="M12 14v6" />
  </svg>
)
const sideIcon = (side: 'left' | 'right') => (
  <svg
    viewBox="0 0 24 24"
    width={20}
    height={20}
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <rect x={side === 'left' ? 3 : 14} y="5" width="7" height="14" rx="1" fill="currentColor" stroke="none" />
  </svg>
)

/**
 * The header controls for a top-level tab screen: Pin in the popup; in the docked panel, a button to
 * move it to the other side and the pin again (lit) to unpin. Nothing at phone width.
 */
export function PinControls() {
  const room = useRoom()
  const docked = useDockedPath()
  const pinned = usePrefs((s) => s.pinned)
  const pinSides = usePrefs((s) => s.pinSides)
  const set = usePrefs((s) => s.set)
  const nav = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  if (room === 0) return null
  const mine = docked && pinned.find((p) => p.path === docked)
  if (mine) {
    const other = mine.side === 'left' ? 'right' : 'left'
    return (
      <>
        <IconButton
          bare
          aria-label={`Move to the ${other}`}
          onClick={() =>
            set({
              pinned: pinTo(pinned, mine.path, other, room),
              pinSides: { ...pinSides, [mine.path]: other },
            })
          }
        >
          {sideIcon(other)}
        </IconButton>
        <IconButton
          bare
          aria-label="Unpin"
          aria-pressed="true"
          style={{ color: 'var(--you)' }}
          onClick={() => set({ pinned: pinned.filter((p) => p.path !== mine.path) })}
        >
          {pinIcon}
        </IconButton>
      </>
    )
  }
  // A thread is the Inbox with it open (bugs.md): pinned, it stays open in the docked copy.
  const thread = /^\/inbox\/[^/]+$/.test(pathname)
  const path = thread ? '/inbox' : PIN_PATHS.find((p) => pathname === p)
  if (!path) return null
  return (
    <IconButton
      bare
      aria-label="Pin beside the map"
      onClick={() => {
        // Each tab docks on its own side: where you last moved it, else its default (the Gate left).
        const p = path as PinPath
        // With the side taken and the other free, it goes to the other (pinTo).
        const want = pinSides[p] ?? PIN_DEFAULT_SIDE[p]
        const free = pinned.every((q) => q.path === p || q.side !== want)
        const side: PinSide =
          free || room < 2 || pinned.filter((q) => q.path !== p).length > 1
            ? want
            : want === 'left'
              ? 'right'
              : 'left'
        set({ pinned: pinTo(pinned, p, side, room) })
        if (!thread) nav({ to: home() })
      }}
    >
      {pinIcon}
    </IconButton>
  )
}
